import React, { useState, useEffect } from "react";
import { Drawer, Button, Spin, message } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import db from "../../database";

/**
 * CompanionPopup
 *
 * Right-side Drawer that slides in when selectedProduct.companion === "Y".
 * Queries IndexedDB getCompanionRules by main_product_id (= selectedProduct.productId).
 *
 * Behaviour:
 *  - No companions found → silently calls onAddToCart(product, null), drawer never shown.
 *  - Companions found    → shows scrollable card list; user clicks "+ Add" on the desired row.
 */
const CompanionPopup = ({ visible, onClose, selectedProduct, onAddToCart }) => {
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoAdded, setAutoAdded] = useState(false);

  // ── Fetch companion rules from IndexedDB ───────────────────────────────────
  useEffect(() => {
    if (!visible || !selectedProduct?.productId) return;

    let cancelled = false;
    setCompanions([]);
    setAutoAdded(false);
    setLoading(true);

    (async () => {
      try {
        const rule = await db.getCompanionRules.where("main_product_id").equals(selectedProduct.productId).first();

        if (cancelled) return;

        const list = rule?.companions || [];

        if (list.length === 0) {
          // No companions — silently add the header product, skip drawing the drawer
          setAutoAdded(true);
          onAddToCart(selectedProduct, null);
          onClose();
        } else {
          setCompanions(list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[CompanionPopup] Failed to load companion rules:", err);
          message.error("Failed to load companion rules.");
          onAddToCart(selectedProduct, null);
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, selectedProduct?.productId]);

  // ── Price fields (header-only, no child calculations) ─────────────────────
  const unitPrice =
    parseFloat(selectedProduct?.salePrice) > 0
      ? parseFloat(selectedProduct?.salePrice)
      : parseFloat(selectedProduct?.sunitprice) > 0
        ? parseFloat(selectedProduct?.sunitprice)
        : parseFloat(selectedProduct?.listPrice) || 0;

  const qty = parseFloat(selectedProduct?.weight) > 0 ? parseFloat(selectedProduct?.weight) : 1;

  const uomDisplay = selectedProduct?.uomData?.[0]?.name || selectedProduct?.uom_name || "";

  const fmtINR = (val) =>
    Number(val).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleAdd = (companion) => {
    onAddToCart(selectedProduct, companion);
    onClose();
  };

  if (autoAdded) return null;

  // ── Design tokens matching the rest of the POS ────────────────────────────
  const navy = "#2F3856";
  const borderColor = "#d9d9d9";
  const font = { fontFamily: "Inter, sans-serif" };

  return (
    <Drawer
      visible={visible}
      placement="right"
      width={480}
      closable={false}
      maskClosable={false}
      onClose={onClose}
      bodyStyle={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#fff",
        ...font,
      }}
      headerStyle={{ display: "none" }}
      footerStyle={{ display: "none" }}
      title={null}
      footer={null}
    >
      {/* ── Custom title bar (matches "Search for Customer" style) ─────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px 18px",
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
        }}
      >
        <div>
          <p
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 700,
              color: navy,
              ...font,
              letterSpacing: "0.3px",
            }}
          >
            {selectedProduct?.name || "Product"}
          </p>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: "12px",
              color: "#888",
              ...font,
            }}
          >
            {[
              (selectedProduct?.upc || selectedProduct?.value) && `Code: ${selectedProduct?.upc || selectedProduct?.value}`,
              (selectedProduct?.uomData?.[0]?.name || selectedProduct?.uom_name) && `UOM: ${selectedProduct?.uomData?.[0]?.name || selectedProduct?.uom_name}`,
              (selectedProduct?.taxCategory || selectedProduct?.taxName) && `Tax: ${selectedProduct?.taxCategory || selectedProduct?.taxName}`,
            ]
              .filter(Boolean)
              .join("  ·  ")}
          </p>
        </div>

        <CloseOutlined
          onClick={onClose}
          style={{
            fontSize: "16px",
            color: navy,
            cursor: "pointer",
            padding: "4px",
            flexShrink: 0,
          }}
        />
      </div>

      {/* ── Scrollable body ────────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "0",
        }}
      >
        {/* Price summary card */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#F3F4F9",
            border: `1px solid ${borderColor}`,
            borderRadius: "5px",
            padding: "14px 18px",
            marginBottom: "24px",
            flexShrink: 0,
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: "12px",
                color: "#777",
                fontWeight: 500,
                ...font,
              }}
            >
              Unit Price
            </p>
            <p
              style={{
                margin: "3px 0 0",
                fontSize: "22px",
                fontWeight: 700,
                color: navy,
                ...font,
              }}
            >
              {fmtINR(unitPrice)}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p
              style={{
                margin: 0,
                fontSize: "12px",
                color: "#777",
                fontWeight: 500,
                ...font,
              }}
            >
              Qty: {qty} {uomDisplay}
            </p>
            <p
              style={{
                margin: "3px 0 0",
                fontSize: "22px",
                fontWeight: 700,
                color: navy,
                ...font,
              }}
            >
              Total: {fmtINR(unitPrice * qty)}
            </p>
          </div>
        </div>

        {/* Section label */}
        <p
          style={{
            margin: "0 0 12px",
            fontSize: "12px",
            fontWeight: 700,
            color: navy,
            letterSpacing: "0.8px",
            ...font,
          }}
        >
          AVAILABLE COMPANIONS
        </p>

        {/* Companion cards */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {companions.map((companion) => (
              <div
                key={companion.companion_product_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px solid ${borderColor}`,
                  borderRadius: "5px",
                  padding: "12px 16px",
                  backgroundColor: "#fff",
                }}
              >
                <div style={{ flex: 1, marginRight: "12px" }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: navy,
                      ...font,
                    }}
                  >
                    {companion.companion_product_name}
                  </p>
                  {companion.capture_qty_required === "Y" && (
                    <p
                      style={{
                        margin: "3px 0 0",
                        fontSize: "12px",
                        color: "#f5a623",
                        fontWeight: 500,
                        ...font,
                      }}
                    >
                      Qty capture required
                    </p>
                  )}
                </div>

                <Button
                  icon={<PlusOutlined />}
                  onClick={() => handleAdd(companion)}
                  style={{
                    backgroundColor: navy,
                    borderColor: navy,
                    color: "#fff",
                    borderRadius: "5px",
                    fontWeight: 500,
                    fontSize: "13px",
                    height: "36px",
                    paddingInline: "16px",
                    flexShrink: 0,
                    ...font,
                  }}
                >
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer: full-width Cancel button (pinned to bottom) ───────────── */}
      <div
        style={{
          flexShrink: 0,
          padding: "16px 24px",
          borderTop: `1px solid ${borderColor}`,
          backgroundColor: "#fff",
        }}
      >
        <Button
          block
          onClick={onClose}
          style={{
            height: "46px",
            borderRadius: "5px",
            backgroundColor: navy,
            borderColor: navy,
            color: "#fff",
            fontSize: "14px",
            fontWeight: 600,
            ...font,
          }}
        >
          Cancel
        </Button>
      </div>
    </Drawer>
  );
};

export default CompanionPopup;
